import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';

function CreateEditEventType() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    getEventType, 
    createEventType, 
    updateEventType, 
    isLoading, 
    generateCacheKey 
  } = useData();
  
  // Better detection of create vs edit mode
  const isCreateMode = location.pathname.includes('/create');
  const isEditing = !isCreateMode && Boolean(id);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#4a90e2',
    isActive: true
  });
  const [error, setError] = useState(null);

  const cacheKey = generateCacheKey('event-type', { eventTypeId: id });
  const loading = isLoading(cacheKey);

  const loadEventType = useCallback(async () => {
    try {
      setError(null);
      console.log('Loading event type with ID:', id);
      const eventType = await getEventType(id);
      console.log('Loaded event type from DataContext:', eventType);
      
      setFormData({
        name: eventType.name || '',
        description: eventType.description || '',
        color: eventType.color || '#4a90e2',
        isActive: eventType.isActive !== false
      });
    } catch (error) {
      console.error('Error loading event type:', error);
      setError('Failed to load event type. Please try again.');
    }
  }, [id, getEventType]);

  useEffect(() => {
    if (isEditing && id && !isCreateMode) {
      loadEventType();
    }
  }, [id, isEditing, isCreateMode, loadEventType]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCancel = () => {
    navigate('/settings/event-types');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      if (isEditing && !isCreateMode) {
        await updateEventType(id, formData);
        console.log('Event type updated via DataContext');
      } else {
        await createEventType(formData);
        console.log('Event type created via DataContext');
      }

      navigate('/settings/event-types');
    } catch (error) {
      console.error('Error saving event type:', error);
      setError(error.message || 'Failed to save event type. Please try again.');
    }
  };

  // Only show loading for valid edit operations
  if (loading && isEditing && !isCreateMode) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading event type...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <h2>{isEditing && !isCreateMode ? 'Edit Event Type' : 'Create Event Type'}</h2>
      
      {error && (
        <div className="error-message" style={{ 
          color: '#dc3545', 
          backgroundColor: 'var(--intent-danger-bg, #f8d7da)', 
          border: '1px solid #f5c6cb', 
          borderRadius: '4px', 
          padding: '0.75rem',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            disabled={loading}
            placeholder="Enter event type name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            disabled={loading}
            placeholder="Enter event type description"
            rows="4"
          />
        </div>

        <div className="form-group">
          <label htmlFor="color">Color</label>
          <input
            type="color"
            id="color"
            name="color"
            value={formData.color}
            onChange={handleInputChange}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              disabled={loading}
            />
            Active
          </label>
        </div>

        <div className="form-actions" style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <button 
            type="submit" 
            disabled={loading || !formData.name.trim()}
            style={{ opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Saving...' : (isEditing && !isCreateMode ? 'Update Event Type' : 'Create Event Type')}
          </button>
          
          <button 
            type="button" 
            className="secondary" 
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateEditEventType;